import { FC, useEffect, useMemo, useRef, useState } from "react";
import { calendarService, IDateTimeSlot, IGetCalendarFilterOptionsResponse } from "../../../entities/calendar";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useMyActions } from "../../../entities/my";
import {
    getTrialCalendarFilter,
    getTrialLessonTagName,
    getTrialOptionItems,
    getTrialRequestErrorMessage,
    getTrialSelectedIdItems,
    IAvailableTrialTutor,
    ITrialLesson,
    trialLessonService,
} from "../../../entities/trialLesson";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { MyError } from "../../../shared/lib/error/MyError";
import { IItem } from "../../../shared/model/types";
import { MyButton, MyButtonSecondary } from "../../../shared/ui/button";
import { ConfirmationAction } from "../../../shared/ui/confirmationAction";
import { DropDownListSelected } from "../../../shared/ui/dropDownSelected";
import { MyInput } from "../../../shared/ui/input";
import { Modal } from "../../../shared/ui/modal";
import { MyTextarea } from "../../../shared/ui/myTextarea";
import {
    createUtcDateFromMoscowInput,
    formatMoscowDateInput,
    formatMoscowTimeInput,
} from "../../../shared/lib/helpers/moscowDateTime";
import { formatTutorConversion, formatTutorLoad } from "../../../shared/lib/helpers/formatTutorLoad";
import classes from './trialLessonReschedule.module.scss'

interface IProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    trialLesson: ITrialLesson;
    onSuccess: () => void;
}

const getTrialLessonTagId = (trialLesson: ITrialLesson): string => {
    if(typeof trialLesson.tag === 'string'){
        return ''
    }

    return trialLesson.tag?.id || ''
}

const SLOT_MINUTES = 30
const MS_IN_MINUTE = 60 * 1000

const getDateTime = (date: string, time: string): Date | null => {
    return createUtcDateFromMoscowInput(date, time)
}

const buildSlot = (startDate: string, startTime: string, endDate: string, endTime: string): IDateTimeSlot | null => {
    const startsAt = getDateTime(startDate, startTime)
    const endsAt = getDateTime(endDate, endTime)

    if(!startsAt || !endsAt) return null

    const durationMinutes = (endsAt.getTime() - startsAt.getTime()) / MS_IN_MINUTE
    if(durationMinutes <= 0 || durationMinutes % SLOT_MINUTES !== 0) return null

    return {
        startsAt: startsAt.toISOString(),
        durationSlots: durationMinutes / SLOT_MINUTES,
    }
}

export const TrialLessonReschedule: FC<IProps> = ({open, setOpen, trialLesson, onSuccess}) => {

    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const trialLessonTagId = useMemo(() => getTrialLessonTagId(trialLesson), [trialLesson])
    const trialLessonTagName = useMemo(() => getTrialLessonTagName(trialLesson), [trialLesson])

    const initialStart = useMemo(() => new Date(trialLesson.startsAt), [trialLesson.startsAt])
    const initialEnd = useMemo(() => (
        new Date(initialStart.getTime() + trialLesson.durationSlots * SLOT_MINUTES * MS_IN_MINUTE)
    ), [initialStart, trialLesson.durationSlots])

    const [startDate, setStartDate] = useState<string>(() => formatMoscowDateInput(initialStart))
    const [startTime, setStartTime] = useState<string>(() => formatMoscowTimeInput(initialStart))
    const [endDate, setEndDate] = useState<string>(() => formatMoscowDateInput(initialEnd))
    const [endTime, setEndTime] = useState<string>(() => formatMoscowTimeInput(initialEnd))
    const [tgAccountId, setTgAccountId] = useState<string>(trialLesson.tgAccount.id)
    const [userId, setUserId] = useState<string>(trialLesson.tutor.userId)
    const [comment, setComment] = useState<string>('')
    const [tgAccountOptions, setTgAccountOptions] = useState<IGetCalendarFilterOptionsResponse['tgAccounts']>([])
    const [availableTutors, setAvailableTutors] = useState<IAvailableTrialTutor[]>([])
    const [isLoadingTgAccounts, setIsLoadingTgAccounts] = useState<boolean>(false)
    const [isLoadingTutors, setIsLoadingTutors] = useState<boolean>(false)
    const [hasSearchedTutors, setHasSearchedTutors] = useState<boolean>(false)
    const [isSaving, setIsSaving] = useState<boolean>(false)
    const [confirm, setConfirm] = useState<boolean>(false)
    const [error, setError] = useState<string>('')

    const tutorsControllerRef = useRef<AbortController | null>(null)

    const tgAccountItems = useMemo<IItem<string>[]>(() => {
        const items = getTrialOptionItems(tgAccountOptions, item => item.title || `@${item.username}`)

        if(!items.some(item => item.id === trialLesson.tgAccount.id)){
            items.unshift({
                id: trialLesson.tgAccount.id,
                name: trialLesson.tgAccount.title || `@${trialLesson.tgAccount.username}`,
            })
        }

        return items
    }, [tgAccountOptions, trialLesson.tgAccount])

    const newSlot = useMemo(() => (
        buildSlot(startDate, startTime, endDate, endTime)
    ), [endDate, endTime, startDate, startTime])

    const tutorItems = useMemo<IItem<string>[]>(() => {
        const items: IItem<string>[] = availableTutors.map(({tutor, conversion, conversionBase, conversionClosedCount, conversionLostCount}) => ({
            id: tutor.userId,
            name: tutor.fullName,
            secondaryText: formatTutorLoad(tutor),
            tertiaryText: formatTutorConversion(conversion, conversionBase, conversionClosedCount, conversionLostCount),
        }))
        const isOriginalSlot = newSlot?.startsAt === new Date(trialLesson.startsAt).toISOString() &&
            newSlot.durationSlots === trialLesson.durationSlots &&
            tgAccountId === trialLesson.tgAccount.id

        if(isOriginalSlot && !items.some(item => item.id === trialLesson.tutor.userId)){
            items.unshift({id: trialLesson.tutor.userId, name: trialLesson.tutor.fullName, secondaryText: formatTutorLoad(trialLesson.tutor)})
        }

        return items
    }, [availableTutors, newSlot, tgAccountId, trialLesson])

    const handleError = (requestError: unknown, fallback: string) => {
        console.log(requestError)

        if(requestError instanceof AuthError){
            setIsAuth(false)
            setGlobalMessage({message: requestError.message, type: 'error'})
        }
        else if(requestError instanceof MyError){
            setGlobalMessage({message: requestError.message, type: 'error'})
        }
        else{
            setError(getTrialRequestErrorMessage(requestError, fallback))
        }
    }

    useEffect(() => {
        if(!open) return

        setStartDate(formatMoscowDateInput(initialStart))
        setStartTime(formatMoscowTimeInput(initialStart))
        setEndDate(formatMoscowDateInput(initialEnd))
        setEndTime(formatMoscowTimeInput(initialEnd))
        setTgAccountId(trialLesson.tgAccount.id)
        setUserId(trialLesson.tutor.userId)
        setAvailableTutors([])
        setHasSearchedTutors(false)
        setIsLoadingTutors(false)
        setComment('')
        setConfirm(false)
        setError('')

        let isActual = true

        const loadTgAccounts = async () => {
            try{
                setIsLoadingTgAccounts(true)
                const response = await calendarService.getCalendarFilterOptions(
                    {selected: getTrialCalendarFilter('', '')},
                )

                if(isActual) setTgAccountOptions(response.tgAccounts)
            }
            catch(requestError){
                if(isActual) handleError(requestError, 'Ошибка при загрузке рабочих Telegram')
            }
            finally{
                if(isActual) setIsLoadingTgAccounts(false)
            }
        }

        loadTgAccounts()

        return () => {
            isActual = false
            tutorsControllerRef.current?.abort()
        }
        // Инициализация формы выполняется только при открытии или смене занятия.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, trialLesson.id])

    const resetTutorSearch = () => {
        tutorsControllerRef.current?.abort()
        setAvailableTutors([])
        setHasSearchedTutors(false)
        setIsLoadingTutors(false)
        setUserId('')
        setError('')
    }

    const findAvailableTutors = async () => {
        if(!newSlot){
            setError('Проверьте дату и время: длительность должна быть кратна 30 минутам')
            return
        }
        if(!tgAccountId){
            setError('Выберите рабочий Telegram')
            return
        }

        const controller = new AbortController()
        tutorsControllerRef.current?.abort()
        tutorsControllerRef.current = controller

        try{
            setError('')
            setAvailableTutors([])
            setHasSearchedTutors(false)
            setIsLoadingTutors(true)

            const response = await trialLessonService.findTrialAvailableTutors({
                slot: newSlot,
                tgAccountId,
                tagId: trialLessonTagId,
                tag: trialLessonTagName,
                tutorGender: 'TUTOR_GENDER_ANY',
            }, {signal: controller.signal})

            if(controller.signal.aborted) return

            setAvailableTutors(response.tutors)
            setHasSearchedTutors(true)
            setUserId(current => {
                const isAvailable = response.tutors.some(({tutor}) => tutor.userId === current)
                const isOriginalSlot = newSlot.startsAt === new Date(trialLesson.startsAt).toISOString() &&
                    newSlot.durationSlots === trialLesson.durationSlots &&
                    tgAccountId === trialLesson.tgAccount.id &&
                    current === trialLesson.tutor.userId

                return isAvailable || isOriginalSlot ? current : ''
            })
        }
        catch(requestError){
            if((requestError as Error).name !== 'AbortError'){
                setAvailableTutors([])
                setHasSearchedTutors(false)
                setUserId('')
                handleError(requestError, 'Ошибка при поиске доступных исполнителей')
            }
        }
        finally{
            if(!controller.signal.aborted) setIsLoadingTutors(false)
        }
    }

    const onClose = (value: boolean) => {
        if(!value){
            setConfirm(false)
            setError('')
        }
        setOpen(value)
    }

    const prepareConfirm = () => {
        if(!newSlot){
            setError('Проверьте дату и время: длительность должна быть кратна 30 минутам')
            return
        }
        if(!tgAccountId){
            setError('Выберите рабочий Telegram')
            return
        }
        if(!hasSearchedTutors){
            setError('Сначала найдите доступных исполнителей для выбранного времени')
            return
        }
        if(!userId){
            setError('Выберите доступного исполнителя')
            return
        }
        if(!comment.trim()){
            setError('Укажите причину переноса')
            return
        }

        setError('')
        setConfirm(true)
    }

    const reschedule = async () => {
        if(!newSlot) return

        try{
            setIsSaving(true)
            await trialLessonService.rescheduleTrialLesson({
                trialLessonId: trialLesson.id,
                newSlot,
                tgAccountId,
                userId,
                comment: comment.trim(),
            })

            setGlobalMessage({message: 'Пробное занятие перенесено', type: 'ok'})
            onClose(false)
            onSuccess()
        }
        catch(requestError){
            handleError(requestError, 'Ошибка при переносе пробного занятия')
        }
        finally{
            setIsSaving(false)
        }
    }

    return (
        <Modal open={open} setOpen={onClose}>
            {confirm ? (
                <ConfirmationAction
                    title="Точно перенести пробное занятие?"
                    setOpen={setConfirm}
                    onClick={reschedule}
                    type="send"
                    isLoading={isSaving}
                />
            ) : (
                <section className={classes.modal}>
                    <section className={classes.header}>
                        <h3>Перенести пробное занятие</h3>
                        <button type="button" className={classes.closeButton} onClick={() => onClose(false)}>×</button>
                    </section>

                    <section className={classes.content}>
                        <section className={classes.formGrid}>
                            <MyInput type="date" title="Дата начала" value={startDate} setValue={(value) => {
                                setStartDate(value)
                                resetTutorSearch()
                            }} setError={setError} />
                            <MyInput type="time" title="Время начала" step={SLOT_MINUTES * 60} value={startTime} setValue={(value) => {
                                setStartTime(value)
                                resetTutorSearch()
                            }} setError={setError} />
                            <MyInput type="date" title="Дата окончания" value={endDate} setValue={(value) => {
                                setEndDate(value)
                                resetTutorSearch()
                            }} setError={setError} />
                            <MyInput type="time" title="Время окончания" step={SLOT_MINUTES * 60} value={endTime} setValue={(value) => {
                                setEndTime(value)
                                resetTutorSearch()
                            }} setError={setError} />
                        </section>

                        <section className={classes.field}>
                            <span>Рабочий Telegram</span>
                            <DropDownListSelected
                                items={tgAccountItems}
                                selectedIdItems={getTrialSelectedIdItems(tgAccountId)}
                                onSelected={(item) => (selected) => {
                                    setError('')
                                    setTgAccountId(selected ? item.id : '')
                                    resetTutorSearch()
                                }}
                                isLoading={isLoadingTgAccounts}
                                search
                            />
                        </section>
                        <section className={classes.formGrid}>
                            <section className={classes.searchButton}>
                                <MyButton
                                    onClick={findAvailableTutors}
                                    isLoading={isLoadingTutors}
                                >
                                    Найти доступных исполнителей
                                </MyButton>
                            </section>

                            {hasSearchedTutors && Boolean(tutorItems.length) && (
                                <section className={classes.field}>
                                    <span>Исполнитель</span>
                                    <DropDownListSelected
                                        items={tutorItems}
                                        selectedIdItems={getTrialSelectedIdItems(userId)}
                                        onSelected={(item) => (selected) => {
                                            setError('')
                                            setUserId(selected ? item.id : '')
                                        }}
                                        isLoading={false}
                                        search
                                    />
                                </section>
                            )}

                            {hasSearchedTutors && !tutorItems.length && (
                                <p className={classes.emptyTutors}>Нет доступных исполнителей на выбранное время</p>
                            )}
                        </section>

                        <MyTextarea
                            title="Причина переноса"
                            value={comment}
                            setValue={setComment}
                            setError={setError}
                            placeholder="Например: перенос по просьбе ученика"
                        />
                    </section>

                    <section className={classes.actions}>
                        <section className={classes.cancelButton}>
                            <MyButtonSecondary onClick={() => onClose(false)}>Отмена</MyButtonSecondary>
                        </section>
                        <section className={classes.submitButton}>
                            <MyButton error={error} onClick={prepareConfirm}>Перенести</MyButton>
                        </section>
                    </section>
                </section>
            )}
        </Modal>
    )
}
